<template>
  <div class="app">
    <app-header
      ref="headerRef"
      @search="handleSearch"
      @logout="handleLogout"
    />

    <main>
      <router-view
        :search-query="searchQuery"
        :search-all-discogs="searchAllDiscogs"
        @clear-search="headerRef?.clearSearch()"
      />
    </main>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AppHeader from '@/components/AppHeader.vue';
import useAuth from '@/composables/useAuth';

// Router
const router = useRouter();

// Search state
const searchQuery = ref('');
const searchAllDiscogs = ref(false);

// Use our auth composable
const {
  user, isAuthenticated, isDiscogsLinked, logout,
} = useAuth();

// Reference to header component
const headerRef = ref(null);

// Handle search from header
const handleSearch = ({ query, searchAllDiscogs: isGlobalSearch }) => {
  searchQuery.value = query;
  searchAllDiscogs.value = isGlobalSearch;
};

// Handle logout
const handleLogout = async () => {
  await logout();
  router.push('/login');
};

// Update header when user changes
watch(user, newUser => {
  if (newUser && headerRef.value) {
    // Convert Firebase user to format expected by header
    const username = newUser.displayName
      || (newUser.email ? newUser.email.split('@')[0] : 'User');

    headerRef.value.onLoginSuccess({
      username,
      avatar_url: newUser.photoURL,
      collection_count: 0,
    });
  }
});

// Watch auth state and redirect as needed
watch([isAuthenticated, isDiscogsLinked], ([newIsAuth, newIsDiscogs]) => {
  const currentRoute = router.currentRoute.value;

  if (newIsAuth && newIsDiscogs && currentRoute.path === '/login') {
    // If authenticated, redirect to home
    router.push('/');
  } else if (!newIsAuth && currentRoute.path !== '/login' && currentRoute.path !== '/callback') {
    // If not authenticated and not on login or callback page, redirect to login
    router.push('/login');
  }
});
</script>

<style>
:root {
  /* Base Colors */
  --color-background: #121212;
  --color-surface: #181818;
  --color-surface-light: #282828;
  --color-surface-lighter: #333333;

  /* Primary Colors */
  --color-primary: #1DB954;
  --color-primary-light: #1ed760;
  --color-primary-dark: #1aa34a;

  /* Text Colors */
  --color-text: #FFFFFF;
  --color-text-secondary: #B3B3B3;
  --color-text-disabled: #727272;
  --color-text-muted: #5e5e5e;

  /* UI Elements */
  --color-divider: rgba(255, 255, 255, 0.1);
  --color-overlay: rgba(0, 0, 0, 0.7);
  --color-hover: rgba(255, 255, 255, 0.1);

  /* Page layout: the header and every page share these so their edges line up */
  --page-max: 1400px;
  --page-gutter: clamp(1rem, 3vw, 2.5rem);

  /* Spacing */
  --space-xs: 0.25rem;
  --space-sm: 0.5rem;
  --space-md: 1rem;
  --space-lg: 1.5rem;
  --space-xl: 2rem;

  /* Typography */
  --font-family:
    'Circular',
    -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell,
    'Open Sans', 'Helvetica Neue',
    sans-serif;
  --font-size-xs: 0.75rem;
  --font-size-sm: 0.875rem;
  --font-size-md: 1rem;
  --font-size-lg: 1.25rem;
  --font-size-xl: 1.5rem;
  --font-size-xxl: 2rem;

  /* Effects */
  --shadow-1: 0 8px 24px rgba(0, 0, 0, 0.5);
  --shadow-2: 0 16px 32px rgba(0, 0, 0, 0.3);
  --shadow-card: 0 8px 16px rgba(0, 0, 0, 0.3);
  --shadow-elevation: 0 2px 4px rgba(0, 0, 0, 0.3);

  /* Border Radius */
  --border-radius-sm: 4px;
  --border-radius: 8px;
  --border-radius-lg: 16px;
  --border-radius-pill: 500px;

  /* Transitions */
  --transition-fast: 0.1s ease;
  --transition-base: 0.2s ease;
  --transition-smooth: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.app {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background);
  overflow-x: hidden;
}

main {
  flex: 1;
  background: linear-gradient(180deg, rgba(40, 40, 40, 0.3) 0%, var(--color-background) 300px);
  min-height: calc(100vh - 60px);
}

/* Global styles */
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  background-color: var(--color-background);
  color: var(--color-text);
}

body {
  font-family: var(--font-family);
  -webkit-font-smoothing: antialiased;
  line-height: 1.5;
  overscroll-behavior: none;
}

* {
  box-sizing: border-box;
}

a {
  color: var(--color-primary);
  text-decoration: none;
  transition: color var(--transition-base);
}

a:hover {
  color: var(--color-primary-light);
  text-decoration: underline;
}

button, input, select, textarea {
  font-family: var(--font-family);
}

button {
  cursor: pointer;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Selection styling */
::selection {
  background-color: var(--color-primary);
  color: white;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 12px;
}

::-webkit-scrollbar-track {
  background: var(--color-background);
}

::-webkit-scrollbar-thumb {
  background: var(--color-surface-light);
  border-radius: 6px;
  border: 3px solid var(--color-background);
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Utility classes */
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
